<script lang="ts">
import {defineComponent} from 'vue'
import Card from "primevue/card";
import {useAuthStore} from "@/stores/auth.store"
import {useThemeStore} from "@/stores/theme.store"
import Button from "primevue/button"
import PanelMenu from "primevue/panelmenu"
import {Keyable} from "@/types/keyable.type"


export default defineComponent({
  name: "LeftMenu",
  components: {
    Card,
    Button,
    PanelMenu
  },
  computed: {
    window() {
      return window
    },
    isImpersonating() {
      return localStorage.getItem('isImpersonating') === 'true' && this.authStore.isLoggedIn
    }
  },
  watch:{
    '$route': 'showCurrentRoute'
  },
  data() {
    return {
      expandedKeys: {} as Record<string, boolean>,
      authStore: useAuthStore(),
      themeStore: useThemeStore(),
      items: [
        {
          key: 'dashboard',
          label: this.$t('student_components.left_menu.dashboard_label'),
          icon: 'pi pi-fw fa-solid fa-tachometer-alt',
          expandable: true,
          route: {name: 'student_dashboard'},
        },
        {
          key: 'events',
          label: this.$t('admin_components.left_menu.events.label'),
          icon: 'pi pi-fw pi-calendar',
          expandable: true,
          items: [
            {label: this.$t('student_components.left_menu.events.sub_items.course_label'), icon: 'pi pi-fw fa-solid fa-calendar-day', route: {name: 'student_course_list'}},
            {label: this.$t('student_components.left_menu.events.sub_items.gala_label'), icon: 'pi pi-fw fa-solid fa-calendar-xmark', route: {name: 'student_gala_list'}},
          ]
        },
      ],
      logoDark: process.env.VUE_APP_LOGO_DARK,
      logoLight: process.env.VUE_APP_LOGO_LIGHT,
    }
  },
  methods: {
    updateExpandedKeys(value: any) {
      localStorage.setItem('leftMenuExpandedKeys', JSON.stringify(value))
    },
    showCurrentRoute() {
      const routeMaps: Keyable  = {
        'student_dashboard': 'dashboard',
        'student_course': 'events',
        'student_gala': 'events',
      }

      if(!this.$router.currentRoute.value.name) return
      const currentRouteSplitted: string[] = this.$router.currentRoute.value.name.toString().split('_')
      const currentRoute: string = currentRouteSplitted[0]+"_"+currentRouteSplitted[1]
      if(routeMaps[currentRoute]){
        this.expandedKeys[routeMaps[currentRoute].toString()] = true
      }
    },
  },
  mounted() {
    const expandedKeys = localStorage.getItem('leftMenuExpandedKeys')
    if(expandedKeys){
      this.expandedKeys = JSON.parse(expandedKeys)
    }
    this.showCurrentRoute()
  }
})
</script>

<template>
  <Card :class="'menu_container' + (window.innerWidth > 750 ? ' show' : '')">
    <template #content>
      <PanelMenu :model="items" class="panel_menu" multiple v-model:expandedKeys="expandedKeys" @update:expandedKeys="updateExpandedKeys">
        <template #item="{ item, props }">
          <RouterLink v-if="item.route" v-slot="{ href, navigate, isExactActive }" :to="item.route" custom>
            <a :href="href" v-bind="props.action" @click="navigate" :class="isExactActive ? 'menu_item router-active': 'menu_item' " aria-hidden="false">
              <span :class="item.icon+' p-menu-item-icon'" />
              <span class="label" :style="item.items ? 'font-weight: bold': ''">{{ item.label }}</span>
              <span v-if="item.items && item.expandable && !expandedKeys[item.key]" class="pi pi-angle-down" />
              <span v-if="item.items && item.expandable && expandedKeys[item.key]" class="pi pi-angle-up" />
            </a>
          </RouterLink>
          <a v-else :href="item.url" :target="item.target" v-bind="props.action" class="menu_item" aria-hidden="false">
            <span :class="item.icon+' p-menu-item-icon'" />
            <span class="label" :style="item.items ? 'font-weight: bold': ''">{{ item.label }}</span>
            <span v-if="item.items && item.expandable && !expandedKeys[item.key]" class="pi pi-angle-down" />
            <span v-if="item.items && item.expandable && expandedKeys[item.key]" class="pi pi-angle-up" />
          </a>
        </template>
      </PanelMenu>
    </template>
    <template #footer>
      <div class="menu_footer">
        <Button :icon="themeStore.getTheme === 'dark' ? 'pi pi-sun': 'pi pi-moon'" severity="contrast" @click="themeStore.toggleTheme()"/>
        <Button v-if="authStore.isLoggedIn && !isImpersonating" icon="pi pi-sign-out" @click="authStore.logout" :fluid="true" severity="danger"/>
      </div>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.menu_container :deep(){
  .p-panelmenu{
    gap: 0;
  }
  .p-panelmenu-panel{
    border: 0;
    background: transparent;
    .p-panelmenu-submenu{
      padding-left: .5rem;
    }
  }
  .p-card-body{
    padding: 0;
    .p-card-footer{
      border-top: 1px solid var(--p-content-border-color)
    }
  }
}
.panel_menu{
  padding: 0.5rem;
}
.menu_item{
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: .2rem;
  padding: .5rem;
  text-decoration: none;
  color: var(--p-panelmenu-item-color);
  .label{
    margin-left: .5rem;
  }
  .pi-angle-down, .pi-angle-up{
    margin-left: auto;
  }
}
.menu_footer{
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  text-align: left;
  display: flex;
  border-top: 1px solid var(--p-menu-border-color);
  gap: 1rem;
  justify-content: center;
}
.router-active{
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: var(--p-panelmenu-item-border-radius);
}
.theme-dark{
  .router-active{
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: var(--p-panelmenu-item-border-radius);
  }
}
</style>
